import {SubscriptionPriceModel} from "../../models/subscriptionPrice";
import React, {useEffect, useState} from "react";
import {useTranslation} from "next-i18next";
import {Body, Heading1, Heading2, Heading3, TextStyles} from "../text";
import Skeleton from "react-loading-skeleton";

export interface AccountSubscriptionOptionViewProps {
  subscription: SubscriptionPriceModel
  availableSubscriptions: SubscriptionPriceModel[]
  isSelected: boolean
  isLoading: boolean
}

export const AccountSubscriptionOptionView: React.FC<AccountSubscriptionOptionViewProps> = ({ subscription, availableSubscriptions, isSelected, isLoading }) => {
  const { t } = useTranslation()

  const formatter = new Intl.NumberFormat('en',{ style: 'currency', currency: subscription.currency })
  const savingsFormatter = new Intl.NumberFormat('en',{ style: 'currency', currency: subscription.currency, maximumFractionDigits: 0 })
  const periodPrice = formatter.format(subscription.price)
  const discountedPeriodPrice = subscription.discountPrice ? formatter.format(subscription.discountPrice) : undefined
  const monthlyPrice = formatter.format(subscription.monthlyPrice)
  const discountedMonthlyPrice: string | undefined = subscription.discountMonthlyPrice ? formatter.format(subscription.discountMonthlyPrice) : undefined

  const [period, setPeriod] = useState<string>()

  const mostExpensiveSubscription = availableSubscriptions.length
    ? availableSubscriptions.reduce((prev, curr) => prev.monthlyPrice > curr.monthlyPrice ? prev : curr)
    : undefined;

  const cheapestSubscription = availableSubscriptions.length
    ? availableSubscriptions.reduce((prev, curr) => prev.monthlyPrice < curr.monthlyPrice ? prev : curr)
    : undefined;

  const isBestOffer = cheapestSubscription?.id === subscription.id

  let yearlySavings: number | undefined;
  if (isBestOffer && mostExpensiveSubscription?.discountMonthlyPrice && subscription.discountMonthlyPrice) {
    yearlySavings = (mostExpensiveSubscription.discountMonthlyPrice * 12) - (subscription.discountMonthlyPrice * 12)
  } else if (isBestOffer && mostExpensiveSubscription) {
    yearlySavings = (mostExpensiveSubscription.monthlyPrice * 12) - (subscription.monthlyPrice * 12)
  }

  useEffect(() => {
    switch (subscription.interval) {
      case "day":
        setPeriod(t('subscription:subscription_title_daily'))
        break
      case "week":
        setPeriod(t('subscription:subscription_title_weekly'))
        break
      case "month":
        if (subscription.intervalCount === 3) {
          setPeriod(t('subscription:subscription_title_quarterly'))
        } else if (subscription.intervalCount === 6) {
          setPeriod(t('subscription:subscription_title_biannually'))
        } else {
          setPeriod(t('subscription:subscription_title_monthly'))
        }
        break
      case "year":
        setPeriod(t('subscription:subscription_title_yearly'))
        break
    }
  }, [subscription])


  return <div className={`w-full rounded-xl border-2 ${isSelected && !isLoading ? 'border-fg-primary text-fg-primary' : 'border-fg-gray-surface'} p-4 lg:p-6 flex space-x-2`}>
    <div className="flex flex-col flex-grow text-left space-y-2">
      <Heading2>{isLoading ? <Skeleton className="!w-20" /> : period}</Heading2>

      <Body>{isLoading ? <Skeleton className="!w-42" /> : t('subscription:subscription_subtitle_billing', { period, price: discountedPeriodPrice ?? periodPrice })}</Body>

      {isBestOffer && <div className="h-1"/>}

      <Heading3 className={`text-fg-primary ${isBestOffer ? '' : 'hidden'}`}>{isLoading ? <Skeleton className="!w-36" /> : t('subscription:subscription_subtitle_save_yearly', { amount: savingsFormatter.format(yearlySavings ?? 0) })}</Heading3>
    </div>

    <div className="flex flex-col space-y-2 flex-shrink-0">
      <Heading1 className="font-bold text-right" >
          <span className="relative">
            {isLoading
              ? <Skeleton className="!w-24" />
              : <span>
                {monthlyPrice}
                <span className={`${discountedMonthlyPrice || isLoading ? 'hidden' : ''} ${TextStyles.HeroBody} lowercase`}>/{t('subscription:subscription_title_month')}</span>
              </span>
            }
            <div className={`${discountedMonthlyPrice ? '' : 'hidden'} absolute left-0 top-1/2 right-0 border-t-6 border-fg-primary transform -rotate-12`} />
          </span>
      </Heading1>

      <Heading2 className={`text-right font-bold ${discountedMonthlyPrice ? '' : 'hidden'}`}>
        {isLoading
          ? <Skeleton className="!w-20" />
          : <span>
                {discountedMonthlyPrice}
            <span className={`${isLoading ? 'hidden' : ''} ${TextStyles.HeroBody} lowercase`}>/{t('subscription:subscription_title_month')}</span>
              </span>
        }
      </Heading2>
    </div>
  </div>
}